import * as React from "react";
import { Head, router, useForm } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthLayout from "@/layouts/AuthLayout";
import { InertiaPageComponent } from "@/types";
import { ArrowLeft } from "lucide-react";

interface GearNewProps {
  gear_types: Array<{ value: string; label: string }>;
  errors?: string[];
  form_data?: any;
}

const GearNew: InertiaPageComponent<GearNewProps> = ({
  gear_types,
  errors: serverErrors,
  form_data,
}) => {
  const { data, setData, post, processing, errors } = useForm({
    gear_item: {
      gear_type: form_data?.gear_type || "",
      nickname: form_data?.nickname || "",
      brand: form_data?.brand || "",
      model: form_data?.model || "",
      notes: form_data?.notes || "",
      is_primary: form_data?.is_primary || false,
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/garage");
  };

  const selectedGearType = gear_types.find(
    (t) => t.value === data.gear_item.gear_type
  );

  return (
    <>
      <Head title="Add Gear - My Garage" />
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => router.visit("/garage")}
              className="mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Garage
            </Button>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
              Add Gear
            </h2>
            <p className="text-gray-600 font-medium">
              Add new equipment to your garage
            </p>
          </div>

          {serverErrors && serverErrors.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <ul className="list-disc list-inside text-sm text-red-700">
                {serverErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <Card className="p-6 border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="gear_type">
                  What type of gear is this? <span className="text-red-500">*</span>
                </Label>
                <select
                  id="gear_type"
                  value={data.gear_item.gear_type}
                  onChange={(e) =>
                    setData("gear_item", { ...data.gear_item, gear_type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                >
                  <option value="">Select gear type</option>
                  {gear_types.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {errors["gear_item.gear_type"] && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors["gear_item.gear_type"]}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="nickname">
                  Give it a nickname <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nickname"
                  type="text"
                  value={data.gear_item.nickname}
                  onChange={(e) =>
                    setData("gear_item", {
                      ...data.gear_item,
                      nickname: e.target.value,
                    })
                  }
                  placeholder="My Fanatec DD Pro"
                  className="mt-1"
                />
                {errors["gear_item.nickname"] && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors["gear_item.nickname"]}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="brand">Brand (optional)</Label>
                  <Input
                    id="brand"
                    type="text"
                    value={data.gear_item.brand}
                    onChange={(e) =>
                      setData("gear_item", {
                        ...data.gear_item,
                        brand: e.target.value,
                      })
                    }
                    placeholder="Fanatec"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="model">Model (optional)</Label>
                  <Input
                    id="model"
                    type="text"
                    value={data.gear_item.model}
                    onChange={(e) =>
                      setData("gear_item", {
                        ...data.gear_item,
                        model: e.target.value,
                      })
                    }
                    placeholder="CSL DD"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <textarea
                  id="notes"
                  value={data.gear_item.notes}
                  onChange={(e) =>
                    setData("gear_item", {
                      ...data.gear_item,
                      notes: e.target.value,
                    })
                  }
                  placeholder="Any notes about this gear..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                  rows={3}
                />
              </div>

              {data.gear_item.gear_type && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_primary"
                    checked={data.gear_item.is_primary}
                    onChange={(e) =>
                      setData("gear_item", {
                        ...data.gear_item,
                        is_primary: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <Label htmlFor="is_primary" className="font-normal">
                    Set as my primary {selectedGearType?.label}
                  </Label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => router.visit("/garage")}
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Adding..." : "Add Gear"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </main>
    </>
  );
};

GearNew.layout = (page: React.ReactNode) => <AuthLayout>{page}</AuthLayout>;

export default GearNew;
